import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CheckCircleIcon, 
  DocumentCheckIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  AcademicCapIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  StarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  CurrencyPoundIcon,
  CalculatorIcon,
  ScaleIcon,
  TrophyIcon,
  FireIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';
import { WebPageSchema } from '../../components/SEO/StructuredData';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';
import PricingComparisonSection from '../../components/pricing/PricingComparisonSection';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function DentalConsentPlans() {
  const heroRef = useRef(null);
  const pricingRef = useRef(null);
  const comparisonRef = useRef(null);
  const faqRef = useRef(null);
  
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('starter_v2');
  const [formData, setFormData] = useState({
    practiceName: '',
    email: '',
    phone: '',
    numberOfDentists: '1-2'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set page meta
  usePageMeta(
    'Dental Consent Pricing Plans | AI-assisted | Unlimited Users | Smile Consent',
    'Plans include AI-assisted consent drafting (clinician-reviewed), unlimited users on supported tiers, digital signing, and consent activity records. No per-user fees on plans where stated. Start your free trial.',
    'https://smileconsent.co.uk/dental-consent-plans'
  );

  // GSAP animations
  useEffect(() => {
    const tl = gsap.timeline();
    
    if (heroRef.current) {
      tl.from(heroRef.current.querySelector('.hero-title'), {
        y: 50,
        opacity: 0,
        duration: 1,
        ease: 'power3.out'
      })
      .from(heroRef.current.querySelector('.hero-subtitle'), {
        y: 30,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out'
      }, '-=0.5')
      .from(heroRef.current.querySelector('.hero-cta'), {
        y: 20,
        opacity: 0,
        duration: 0.6,
        ease: 'power3.out'
      }, '-=0.3');
    }

    // Scroll-triggered animations
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(element, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert(`Thank you for your interest in SmileConsent! We'll contact you at ${formData.email} within 24 hours to set up your free trial for ${formData.practiceName}. Please check your email for confirmation.`);
      setFormData({ practiceName: '', email: '', phone: '', numberOfDentists: '1-2' });
      setShowRegistrationForm(false);
      setIsSubmitting(false);
    }, 2000);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };


  const competitorComparison = [
    {
      feature: 'Unlimited Users',
      smileConsent: '✓ Included in all plans',
      competitors: '£50-200 per user/month',
      savings: 'Save £600-2400/year per user'
    },
    {
      feature: 'Consent generation',
      smileConsent: 'AI-generated, UK-law ready',
      competitors: 'Static forms only',
      savings: 'Tailored to each patient & treatment'
    },
    {
      feature: 'Setup & Training',
      smileConsent: 'Free setup & training',
      competitors: '£500-2000 setup fee',
      savings: 'Save £500-2000 upfront'
    },
    {
      feature: 'Support',
      smileConsent: 'Priority support included',
      competitors: '£100-300/month for support',
      savings: 'Save £1200-3600/year'
    },
    {
      feature: 'Customization',
      smileConsent: 'Full customization included',
      competitors: '£200-500 per customization',
      savings: 'Save on every customization'
    },
    {
      feature: 'Integration',
      smileConsent: 'Free basic integrations',
      competitors: '£300-800 per integration',
      savings: 'Save on every integration'
    }
  ];

  const faqItems = [
    {
      question: 'Do you really offer unlimited users?',
      answer: 'Yes! Unlike our competitors who charge per user, all our plans include unlimited users at no extra cost. Add as many dentists, hygienists, and staff as you need without worrying about per-user fees.'
    },
    {
      question: 'What happens if I exceed my consent limit?',
      answer: 'We\'ll notify you when you\'re approaching your limit and offer options to upgrade or purchase additional consents. We never cut off your service without warning.'
    },
    {
      question: 'Can I change plans anytime?',
      answer: 'Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate any billing differences.'
    },
    {
      question: 'Is there a setup fee?',
      answer: 'No setup fees! We include free setup, training, and basic customization with all plans. Our team will help you get started at no extra cost.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, bank transfers, and can arrange direct debit for larger practices. All payments are processed securely.'
    },
    {
      question: 'Do you offer discounts for annual payments?',
      answer: 'Yes! Save 20% when you pay annually. Contact our sales team for custom pricing for multi-practice groups.'
    }
  ];

  const pricingBenefits = [
    {
      title: 'Transparent Pricing',
      description: 'No hidden fees, no per-user charges, no surprise costs. What you see is what you pay.',
      icon: '💰'
    },
    {
      title: 'Unlimited Users',
      description: 'Add as many dentists, hygienists, and staff as you need without worrying about per-user fees.',
      icon: '👥'
    },
    {
      title: '14-Day Free Trial',
      description: 'Try all features risk-free for 14 days. No credit card required to get started.',
      icon: '🆓'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 opacity-100">
              Choose Your Perfect Plan
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-4 max-w-4xl mx-auto opacity-100">
              Transparent pricing with no hidden costs. Choose the plan that fits your practice.
            </p>
            <p className="text-sm text-gray-500 mb-8 max-w-2xl mx-auto opacity-100">
              All plans include unlimited clinicians — no per-seat fees.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-100">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <SparklesIcon className="h-6 w-6" />
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PricingComparisonSection
        sectionRef={pricingRef}
        animate
        subtitle="Choose the plan that fits your practice. All plans include unlimited clinicians — no per-seat fees."
      />

      {/* Competitor Comparison Section */}
      <section ref={comparisonRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why SmileConsent is More Cost-Effective
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Compare our transparent pricing with competitors who charge per user and hidden fees.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-on-scroll">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-green-600">SmileConsent</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-red-600">Competitors</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-blue-600">Your Savings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {competitorComparison.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.feature}</td>
                      <td className="px-6 py-4 text-sm text-green-600">{item.smileConsent}</td>
                      <td className="px-6 py-4 text-sm text-red-600">{item.competitors}</td>
                      <td className="px-6 py-4 text-sm text-blue-600 font-medium">{item.savings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </section>

      {/* Pricing Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Our Pricing is Different
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent, fair pricing with no hidden costs or per-user fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingBenefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl animate-on-scroll text-center">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our pricing and plans.
            </p>
          </div>

          <div className="space-y-6">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg animate-on-scroll">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.question}</h3>
                <p className="text-gray-600">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Save Money on Your Consent Management?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join hundreds of dental practices saving thousands with our unlimited users policy. 
            Start your free trial today - no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-6 w-6" />
              Start Free Trial
            </Link>
            <Link
              to="/dental-consent-forms"
              className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <DocumentTextIcon className="h-6 w-6" />
              View Templates
            </Link>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Start Your Free Trial</h3>
              <button
                onClick={() => setShowRegistrationForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Practice Name</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Dentists</label>
                <select
                  name="numberOfDentists"
                  value={formData.numberOfDentists}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1-2">1-2 Dentists</option>
                  <option value="3-5">3-5 Dentists</option>
                  <option value="6-10">6-10 Dentists</option>
                  <option value="10+">10+ Dentists</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Start Free Trial'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">SmileConsent</h3>
              <p className="text-gray-400 text-sm">
                Professional dental consent management for UK practices. Unlimited users, no per-user fees.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Solutions</h3>
              <ul className="space-y-2">
                <li><Link to="/dental-consent-forms" className="text-gray-400 hover:text-white text-sm">Consent Forms</Link></li>
                <li><Link to="/dental-consent-plans" className="text-gray-400 hover:text-white text-sm">Consent Plans</Link></li>
                <li><Link to="/dental-compliance-software" className="text-gray-400 hover:text-white text-sm">Compliance Software</Link></li>
                <li><Link to="/digital-consent-dentists" className="text-gray-400 hover:text-white text-sm">Digital Consent</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white text-sm">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link></li>
                <li><Link to="/help" className="text-gray-400 hover:text-white text-sm">Help Center</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-white text-sm">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <CompanyDisclosure tone="dark" />
          </div>
        </div>
      </footer>
    </div>
  );
}